/** @jsx jsx */
import {
  BookmarkIcon,
  Box,
  Button,
  CloseIcon,
  Container,
  Flex,
  HamburgerIcon,
  Link,
  Text,
} from '@bottlebooks/gatsby-theme-base/src';
import { useFavorites } from '@bottlebooks/gatsby-plugin-bookmarks/src';
import Logo from '@bottlebooks/gatsby-theme-event/src/components/Layout/Logo';
import MenuActions from '@bottlebooks/gatsby-theme-event/src/components/Layout/MenuActions';
import { ShoppingCartIndicator } from '@bottlebooks/gatsby-theme-event/src/components/Shop/ShoppingCartMenu';
import SiteSearch from '@bottlebooks/gatsby-theme-event/src/components/SiteSearch/SiteSearch';
import useSnipcart from '@bottlebooks/gatsby-theme-event/src/useSnipcart';
import React, { useState } from 'react';
import { jsx } from 'theme-ui';
import useSiteLanguage from '../../../../useSiteLanguage';
import MenuItem from './MenuItem';

/**
 * @param {object} opt
 * @param {object[]} opt.menuItems
 * @param {string} opt.landingPage
 * @param {boolean} [opt.isScrolledDown]
 * @param {string} opt.locale
 * @returns
 */
export default function MainMenuMobile({
  menuItems,
  landingPage,
  isScrolledDown,
  locale,
  ...rest
}) {
  const [menuIsVisible, setMenuIsVisible] = useState(false);
  const [searchIsVisible, toggleSearchIsVisible] = useState(false);

  return (
    <Container {...rest}>
      <Flex>
        <Flex direction="column" sx={{ width: '100%' }}>
          <Flex align="center">
            <Box>
              <Button
                variant="text"
                onClick={() => setMenuIsVisible(!menuIsVisible)}
              >
                {menuIsVisible ? <CloseIcon /> : <HamburgerIcon />}
              </Button>
            </Box>
            <Box sx={{ marginLeft: 3 }}>
              <Logo to={landingPage} isScrolledDown={isScrolledDown} />
            </Box>

            <MenuActions
              searchIsVisible={searchIsVisible}
              toggleSearchIsVisible={toggleSearchIsVisible}
            />
          </Flex>
        </Flex>
      </Flex>
      {searchIsVisible && (
        <Search
          locale={locale}
          onClick={() => toggleSearchIsVisible(!searchIsVisible)}
        />
      )}
      {menuIsVisible && (
        <Menu
          menuItems={menuItems}
          onClick={() => setMenuIsVisible(!menuIsVisible)}
        />
      )}
    </Container>
  );
}

function Menu({ onClick, menuItems }) {
  const { favorites: bookmarks } = useFavorites();
  const { openCart } = useSnipcart();
  const { setLang } = useSiteLanguage();
  return (
    <Box
      sx={{
        position: 'fixed',
        paddingX: 2, // Match header padding
        paddingBottom: 3,
        zIndex: 'modal',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'light',
        overflowY: 'scroll',
      }}
    >
      <Flex direction="column">
        <Button
          variant="text"
          onClick={onClick}
          sx={{
            marginRight: 'auto',
            marginTop: 2.5,
            marginLeft: 2,
          }}
        >
          <CloseIcon />
        </Button>
        <Flex
          direction="column"
          sx={{
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              target={index < 2 ? '_blank' : ''}
              {...item}
            />
          ))}
          {/* <MenuItem type="bookmarks" sx={{ position: 'relative' }}>
            <React.Fragment>
              <Text sx={{ marginRight: 1, display: 'inline-block' }}>
                <Trans>My list</Trans>
              </Text>
              <BookmarkIcon
                variant={bookmarks?.length ? 'inverted' : 'hollow'}
                size="small"
                sx={{
                  color: bookmarks?.length ? 'primary' : null,
                  marginLeft: 1,
                }}
              />

              {Boolean(bookmarks?.length) && (
                <Text
                  variant="small"
                  sx={{
                    display: 'inline-block',
                    // position: 'absolute',
                    marginLeft: 1,
                    verticalAlign: 'super',
                    marginTop: -3,
                    color: 'primary',
                  }}
                >
                  {bookmarks?.length}
                </Text>
              )}
            </React.Fragment>
          </MenuItem> */}
          {process.env.GATSBY_SNIPCART_API_KEY && (
            <MenuItem
              onClick={() => openCart()}
              // Conflict between menu and text variant requires manual override
              sx={{
                padding: 2,
                border: 0,
                backgroundColor: 'transparent',
              }}
            >
              <ShoppingCartIndicator variant="small">
                <Trans>Cart</Trans>
              </ShoppingCartIndicator>
            </MenuItem>
          )}
          <Flex
            direction="column"
            sx={{
              marginTop: 3,
              paddingTop: 3,
              borderTop: 1,
              borderColor: 'brand.smoke',
            }}
          >
            <Text>Language</Text>
            <Flex justify="center" gap={3}>
              <Link
                onClick={() => setLang({ lang: 'en' })}
                className="notranslate"
              >
                <Text>EN</Text>
              </Link>
              <Link
                onClick={() => setLang({ lang: 'fr' })}
                className="notranslate"
              >
                <Text>FR</Text>
              </Link>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

function Search({ onClick, locale }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        paddingBottom: 3,
        zIndex: 'modal',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'light',
        overflowY: 'scroll',
      }}
    >
      <Flex
        gap={1}
        align="center"
        sx={{
          backgroundColor: 'background',
          padding: 2,
          borderBottom: 1,
          borderColor: 'border',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <SiteSearch
            variant="mobile"
            locale={locale}
            placeholder={`Search for producer or wine…`}
            autoFocus
            sx={{ border: 0 }}
          />
        </Box>
        <Button
          variant="text"
          onClick={onClick}
          sx={{ padding: 2, display: 'flex', alignItems: 'center' }}
        >
          <CloseIcon size="small" />
        </Button>
      </Flex>
    </Box>
  );
}

function Trans({ children }) {
  return <React.Fragment>{children}</React.Fragment>;
}
